import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { errorToster } from "../../shared/toster/Toster";
import { CancelOutlined, FileUploadOutlined } from "@mui/icons-material";
import IconConstants from "../../utils/IconConstants";
import { Helper } from "../../utils/Helpers";
import { EmployeePersonalInfoSchema } from "../../config/forms/employeeFormConfig";
import ApiClient from "../../apis/Api-client";
import { END_POINT } from "../../utils/End-points";

const EmployeePersonalInfoForm = ({
  textFieldStyles,
  errors,
  touchedFields = {},
  isSubmitted,
  register,
  watch,
  setValue,
  setCurrentSchema,
  getValues,
  selectedImage,
  setSelectedImage = () => { },
  allCountries,
  states,
  setStates = () => { },
  cities,
  setCities = () => { },
  sameAsPermanentAdd,
  setSameAsPermanentAdd = () => { },
  proofTypes,
}) => {
  const { api } = ApiClient();
  const { SUPPORTED_ATTACHMENTS_FORMATS, convertToSelectFieldValues } = useMemo(() => new Helper(), []);

  const [maritalStatus, setMaritalStatus] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [workTypes, setWorkTypes] = useState([]);
  const [idProofFrontPreview, setIdProofFrontPreview] = useState(null);
  const [idProofBackPreview, setIdProofBackPreview] = useState(null);

  const permanentAddress = watch("permanent_address");
  const country = watch("country");
  const state = watch("state");
  const proofTypeId = watch("proof_type_id");
  const watchedIdProofFront = watch("id_proof_front");
  const watchedIdProofBack = watch("id_proof_back");

  const shouldShowError = useCallback((fieldName) => !!errors[fieldName] && (touchedFields[fieldName] || isSubmitted), [errors, touchedFields, isSubmitted]);

  useEffect(() => {
    const updatePreview = (file, setPreview) => {
      if (!file) { setPreview(null); return null; }
      if (typeof file === "string") { setPreview(file); return null; }
      const url = URL.createObjectURL(file);
      setPreview(url);
      return url;
    };
    const urlFront = updatePreview(watchedIdProofFront, setIdProofFrontPreview);
    const urlBack = updatePreview(watchedIdProofBack, setIdProofBackPreview);
    return () => {
      if (urlFront) URL.revokeObjectURL(urlFront);
      if (urlBack) URL.revokeObjectURL(urlBack);
    };
  }, [watchedIdProofFront, watchedIdProofBack]);

  useEffect(() => {
    setCurrentSchema(EmployeePersonalInfoSchema);
    const fetchMasters = async () => {
      try {
        const [ms, dept, des, wt] = await Promise.all([
          api.get(END_POINT.EMPLOYEE.GET_ALL_MARITAL_STATUS),
          api.get(END_POINT.EMPLOYEE.GET_ALL_DEPARTMENT),
          api.get(END_POINT.EMPLOYEE.GET_ALL_DESIGNATION),
          api.get(END_POINT.EMPLOYEE.GET_ALL_WORK_TYPE)
        ]);
        if (ms?.status === 200) setMaritalStatus(convertToSelectFieldValues(ms.data.data));
        if (dept?.status === 200) setDepartments(convertToSelectFieldValues(dept.data.data));
        if (des?.status === 200) setDesignations(convertToSelectFieldValues(des.data.data));
        if (wt?.status === 200) setWorkTypes(convertToSelectFieldValues(wt.data.data));
      } catch (e) { console.error("Error fetching masters", e); }
    };
    fetchMasters();
  }, []);

  useEffect(() => {
    if (getValues("proof_number")) setValue("proof_number", getValues("proof_number"), { shouldValidate: true });
  }, [proofTypeId]);

  useEffect(() => {
    if (country) {
      api.get(`${END_POINT.UTILS.GET_STATE}/${country}`).then(res =>
        setStates(res?.data?.data?.map(s => ({ label: s.state_name, value: s.state_id })) || [])
      );
    }
  }, [country]);

  useEffect(() => {
    if (state) {
      api.get(`${END_POINT.UTILS.GET_CITY}/${state}`).then(res =>
        setCities(res?.data?.data?.map(c => ({ label: c.city_name, value: c.city_id })) || [])
      );
    }
  }, [state]);

  const handleAddressCheckboxChange = (e) => {
    setSameAsPermanentAdd(e.target.checked);
    if (e.target.checked && permanentAddress) setValue("present_address", permanentAddress, { shouldValidate: true });
  };

  const validateAndSetFile = (file, fieldName, setPreview) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/jpg"].includes(file.type)) return errorToster("Only JPEG, JPG and PNG are allowed");
    if (file.size > 5 * 1024 * 1024) return errorToster("File size must be under 5MB");
    if (setPreview) setPreview(URL.createObjectURL(file));
    setValue(fieldName, file, { shouldValidate: true });
  };

  const personalFields = useMemo(() => [
    { name: "employee_name", label: "Name", sm: 6 },
    { name: "father_name", label: "Father Name", sm: 6 },
    { name: "dob", label: "Date of Birth", sm: 6, type: "date" },
    { name: "gender", label: "Gender", sm: 6, type: "select", options: [{ label: "Male", value: "Male" }, { label: "Female", value: "Female" }, { label: "Other", value: "Other" }] },
    { name: "mobile", label: "Mobile Number", sm: 6 },
    { name: "alternative_mobile", label: "Alternative Mobile Number", sm: 6 },
    { name: "marital_status_id", label: "Marital Status", sm: 6, type: "select", options: maritalStatus },
    { name: "email", label: "Email", sm: 6 },
    { name: "country", label: "Country", sm: 4, type: "select", options: allCountries },
    { name: "state", label: "State", sm: 4, type: "select", options: states, disabled: !country },
    { name: "city_id", label: "City", sm: 4, type: "select", options: cities, disabled: !state },
    { name: "permanent_address", label: "Permanent Address", sm: 12, multiline: true, rows: 3 },
    { name: "present_address", label: "Present Address", sm: 12, multiline: true, rows: 3, readOnly: sameAsPermanentAdd, shrink: !!watch("present_address") },
    { name: "proof_type_id", label: "Proof Type", sm: 6, type: "select", options: proofTypes },
    { name: "proof_number", label: "Proof Number", sm: 6 },
    { name: "pan_no", label: "PAN Number", sm: 6 },
    { name: "designation_id", label: "Designation", sm: 6, type: "select", options: designations },
    { name: "department_id", label: "Department", sm: 6, type: "select", options: departments },
    { name: "employment_type_id", label: "Employment Type", sm: 6, type: "select", options: workTypes },
    { name: "date_of_joining", label: "Date of Joining", sm: 6, type: "date" },
  ], [maritalStatus, allCountries, states, cities, proofTypes, sameAsPermanentAdd, designations, departments, workTypes, country, state, watch("present_address")]);

  const renderField = useCallback((field) => {
    const commonProps = {
      ...register(field.name),
      error: shouldShowError(field.name),
      helperText: shouldShowError(field.name) ? errors[field.name]?.message : "",
      label: field.label,
      fullWidth: true,
      size: "small",
      InputProps: { ...textFieldStyles.input, ...(field.readOnly && { readOnly: field.readOnly }) },
      InputLabelProps: field.type === "date" ? { ...textFieldStyles.label, shrink: true } : { ...textFieldStyles.label, ...(field.shrink && { shrink: field.shrink }) },
      FormHelperTextProps: textFieldStyles.helperText,
      multiline: field.multiline,
      rows: field.rows,
      disabled: field.disabled,
    };

    if (field.type === "select") {
      return (
        <TextField {...commonProps} select value={watch(field.name) || ""} SelectProps={{ MenuProps: { PaperProps: { sx: { "& .MuiMenuItem-root:hover": { backgroundColor: "#DFA828", color: "white" } } } } }}>
          {field.options?.map((item, key) => (
            <MenuItem key={key} value={field.name === "proof_type_id" ? item.label : (item.value ?? item.label)}>
              {item.label}
            </MenuItem>
          ))}
        </TextField>
      );
    }
    return <TextField {...commonProps} type={field.type || "text"} />;
  }, [register, shouldShowError, errors, textFieldStyles, watch]);

  return (
    <Grid container sx={{ p: " 2% 4%", width: "100%", gap: "4%" }}>
      <Grid item xs={12}><Typography variant="h6" color="#DFA828" sx={{ fontWeight: 600, mb: 2 }}>Personal Information</Typography></Grid>
      <Grid item xs={12} sm={7}>
        <Grid container spacing={2}>
          {personalFields.map((field) => (
            <React.Fragment key={field.name}>
              {field.name === "present_address" && (
                <Grid item xs={12}>
                  <FormControlLabel control={<Checkbox checked={sameAsPermanentAdd} onChange={handleAddressCheckboxChange} color="primary" disabled={!permanentAddress || !!errors.permanent_address} />} label="Permanent address same as present address" sx={{ mb: 1 }} />
                </Grid>
              )}
              <Grid item xs={12} sm={field.sm}>{renderField(field)}</Grid>
            </React.Fragment>
          ))}
        </Grid>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Typography variant="body2" color="#747474" sx={{ fontWeight: 600, mb: "5px" }}>Upload Employee Photo</Typography>
        <Box sx={{ height: "150px", width: "100%", mb: 1, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#FFF6E8", borderRadius: "10px", cursor: "pointer", position: "relative", overflow: "hidden", border: !!errors.employee_photo ? "2px dashed red" : "2px dashed #DFA828" }} onClick={() => document.getElementById("photo-upload").click()}>
          {selectedImage ? (
            <>
              <img src={selectedImage} alt="Profile" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              <IconButton size="small" sx={{ position: "absolute", top: 5, right: 5, bgcolor: "rgba(255,255,255,0.7)", "&:hover": { bgcolor: "white" } }} onClick={(e) => { e.stopPropagation(); setSelectedImage(null); setValue("employee_photo", null); }}><CancelOutlined sx={{ fontSize: 18, color: "#DFA828" }} /></IconButton>
            </>
          ) : <Typography variant="caption" color={!!errors.employee_photo ? "error" : "#DFA828"}>Click to upload photo</Typography>}
          <input type="file" id="photo-upload" hidden accept="image/*" onChange={(e) => validateAndSetFile(e.target.files[0], "employee_photo", setSelectedImage)} />
        </Box>
        {errors.employee_photo && <Typography variant="caption" color="error" sx={{ mb: 2, display: 'block' }}>{errors.employee_photo.message}</Typography>}

        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: "#747474" }}>ID Proof Attachments (Front & Back)</Typography>
        <Grid container spacing={2}>
          {[{ label: "Front side", preview: idProofFrontPreview, id: "front", name: "id_proof_front" }, { label: "Back side", preview: idProofBackPreview, id: "back", name: "id_proof_back" }].map(side => (
            <Grid item xs={12} key={side.id}>
              <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 500 }}>{side.label}</Typography>
              <Box sx={{ height: "180px", border: !!errors[side.name] ? "2px dashed red" : "2px dashed #DFA828", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", mt: 0.5, cursor: "pointer", position: "relative", overflow: "hidden", backgroundColor: "#FFF6E8" }} onClick={() => document.getElementById(`id-${side.id}`).click()}>
                {side.preview ? (
                  <>
                    <img src={side.preview} alt={side.label} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    <IconButton size="small" sx={{ position: "absolute", top: 5, right: 5, bgcolor: "rgba(255,255,255,0.7)", "&:hover": { bgcolor: "white" } }} onClick={(e) => { e.stopPropagation(); side.id === "front" ? setValue("id_proof_front", null) : setValue("id_proof_back", null); }}><CancelOutlined sx={{ fontSize: 18, color: "#DFA828" }} /></IconButton>
                  </>
                ) : <Typography variant="caption" sx={{ fontSize: "12px", color: !!errors[side.name] ? "error" : "#DFA828" }}>Click to upload {side.label}</Typography>}
                <input type="file" id={`id-${side.id}`} hidden accept="image/*" onChange={(e) => validateAndSetFile(e.target.files[0], side.name)} />
              </Box>
              {errors[side.name] && <Typography variant="caption" color="error">{errors[side.name].message}</Typography>}
            </Grid>
          ))}
        </Grid>

      </Grid>
    </Grid>
  );
};

export default EmployeePersonalInfoForm;